import React, { useContext, useEffect, useMemo, useRef, useState, Suspense } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Avatar,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
} from '@material-ui/core';
import InputBase from '@material-ui/core/InputBase';
import { preloadMicRecorder } from '../lib/micRecorderShim';

// Ícones
import DashboardIcon from '@material-ui/icons/Dashboard';
import HomeIcon from '@material-ui/icons/Home';
import ContactsIcon from '@material-ui/icons/Contacts';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import BuildIcon from '@material-ui/icons/Build';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import BusinessCenterIcon from '@material-ui/icons/BusinessCenter';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CachedIcon from '@material-ui/icons/Cached';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import PersonIcon from '@material-ui/icons/Person';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import MenuIcon from '@material-ui/icons/Menu';
// Ícones oficiais @material-ui/icons (abrir/fechar menu lateral)
import FormatIndentDecreaseIcon from '@material-ui/icons/FormatIndentDecrease';
import FormatIndentIncreaseIcon from '@material-ui/icons/FormatIndentIncrease';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ChatIcon from '@material-ui/icons/Chat';
import ForumIcon from '@material-ui/icons/Forum';
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer';
import SearchIcon from '@material-ui/icons/Search';
import MessageIcon from '@material-ui/icons/Message';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ScheduleIcon from '@material-ui/icons/Schedule';
import QueueIcon from '@material-ui/icons/Queue';
import ListIcon from '@material-ui/icons/List';
import SettingsIcon from '@material-ui/icons/Settings';
import ReceiptIcon from '@material-ui/icons/Receipt';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import BusinessIcon from '@material-ui/icons/Business';
import PaletteIcon from '@material-ui/icons/Palette';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import OndemandVideoIcon from '@material-ui/icons/OndemandVideo';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import CategoryIcon from '@material-ui/icons/Category';
import TuneIcon from '@material-ui/icons/Tune';
import LoyaltyIcon from '@material-ui/icons/Loyalty';
import ExtensionIcon from '@material-ui/icons/Extension';
import AppsIcon from '@material-ui/icons/Apps';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
// import HistoryIcon from '@material-ui/icons/History';
import CampaignIcon from '@mui/icons-material/Campaign';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TimerIcon from '@material-ui/icons/Timer';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SecurityIcon from '@material-ui/icons/Security';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SendIcon from '@material-ui/icons/Send';
import AddIcon from '@material-ui/icons/Add';
import { FaWhatsapp } from 'react-icons/fa';

import useSettings from '../hooks/useSettings';
import { AuthContext } from '../context/Auth/AuthContext';
import { usePlanPermissions } from '../context/PlanPermissionsContext.jsx';
import NotificationsDrawer from '../components/NotificationsDrawer/index.jsx';
import { APP_PATHS } from '../constants/appPaths';
import { updateMyProfile } from '../services/userService';
import NotificationModal from '../components/NotificationModal';
import BlockedModal from '../components/BlockedModal';
import TrialEndingModal from '../components/TrialEndingModal';
import TrialBanner from '../components/TrialBanner';
import {
  shouldShowTrialEndingModal,
  wasTrialEndingModalShownToday,
  markTrialEndingModalShownToday,
} from '../utils/trialEndingUtils';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import PwaHeadUpdater from '../components/PwaHeadUpdater';
import PwaReinstallModal from '../components/PwaReinstallModal';
import SearchTicketModal from '../components/SearchTicketModal/index.jsx';
import InternalChatWidget from '../components/InternalChatWidget/index.jsx';
import TeamGoalChip from '../components/TeamGoalChip';
import NewTicketModal from '../components/NewTicketModal';
import { TicketsContext } from '../context/Tickets/TicketsContext';
import UsersPresencePanel from '../components/UsersPresencePanel';
import { getBackendUrl } from '../config';
import {
  SUBSCRIPTION_PATH_BY_SECTION,
  SUBSCRIPTION_SECTIONS,
  isSubscriptionAreaPath,
} from '../pages/Subscription/constants';
import { unsubscribeAndUnregister } from '../services/pushSubscriptionService';
import { canManageSubscription } from '../utils/subscriptionAccess';
import logo from '../assets/logo.png';
import useStyles, {
  collapsedDrawerWidth,
  expandedDrawerWidth,
  TRIAL_BANNER_HEIGHT,
} from './styles';
import usePermission from '../hooks/usePermission';

const backendUrl = getBackendUrl();

const LoggedInLayout = ({ children }) => {
  const { get: getSetting } = useSettings();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();
  const location = useLocation();

  // Aquece o script de gravação de áudio em idle: fora do caminho crítico do
  // login, mas pronto antes da primeira gravação (evita atraso/hiccup ao gravar).
  useEffect(() => {
    preloadMicRecorder();
  }, []);
  const [drawerExpanded, setDrawerExpanded] = useState(true);
  const drawerInitialized = useRef(false);
  const [expandedMenus, setExpandedMenus] = useState(() => ({
    sistema: location.pathname.startsWith('/system') || location.pathname.startsWith('/sistema'),
    superAdmin: location.pathname.startsWith('/super-admin'),
    ajuda: location.pathname === '/helps' || location.pathname === '/tutorial-videos',
    vendas: location.pathname.startsWith('/sales') || location.pathname.startsWith('/vendas'),
    marketing:
      location.pathname.startsWith('/marketing') || location.pathname.startsWith('/campanhas'),
    contatos:
      location.pathname.startsWith('/contacts') || location.pathname.startsWith('/contatos'),
    atendimentos:
      location.pathname.startsWith('/tickets') || location.pathname.startsWith('/chats'),
    automacoes: location.pathname.startsWith('/automations'),
    crm: location.pathname.startsWith('/crm'),
    tasks: location.pathname.startsWith('/tasks'),
  }));
  const drawerWidth = isMobile
    ? expandedDrawerWidth
    : drawerExpanded
      ? expandedDrawerWidth
      : collapsedDrawerWidth;
  const showMenuLabels = drawerWidth > collapsedDrawerWidth || isMobile;
  const primaryColor = theme.palette.primary.main || '#3b82f6';
  const classes = useStyles({
    drawerWidth,
    primaryColor,
    sistemaExpanded: expandedMenus.sistema,
    superAdminExpanded: expandedMenus.superAdmin,
  });

  const { user, handleLogout, loading, isMobileSession } = useContext(AuthContext);
  const { allowed: canReadUsers } = usePermission('users:read');
  const { allowed: canCreateTicket } = usePermission('tickets:create');
  const { setCurrentTicket, setTabOpen } = useContext(TicketsContext);
  const {
    planActive,
    hasApp,
    loading: planLoading,
    trialActive,
    trialExpiringSoon,
    openAi: planOpenAi,
    integrations: planIntegrations,
    schedules: planSchedules,
    internalChat: planInternalChat,
    tasks: planTasks,
    products: planProducts,
    allowAiPlatformCredits,
  } = usePlanPermissions();
  const userPermissions = useMemo(
    () => new Set(Array.isArray(user?.permissions) ? user.permissions : []),
    [user?.permissions]
  );
  // Feature de plano: aparece se o plano tem a flag (ou super). Sem vazamento de
  // "profile === admin" — admin sem o plano tomaria 403 no backend (requirePlanFeature).
  const userCanUseTasks = Boolean(user?.super) || planTasks;
  // Produtos é feature de plano (Plan.useProducts) — alinhado ao gate do backend
  // requirePlanFeature("useProducts"), que libera só super. Sem o vazamento de
  // "profile === admin" que tasks tem (admin sem o plano tomaria 403).
  const userCanUseProducts = Boolean(user?.super) || planProducts;

  const isSuperAdminUser = Boolean(user && user.super);
  const isOwnerUser = Boolean(user?.isOwner);
  // God mode RBAC: só owner e super veem tudo sem filtro de permissões.
  const isGodMode = isSuperAdminUser || isOwnerUser;
  const canManageSubscriptionAccess = canManageSubscription(user);
  const isBlockedRoute =
    location.pathname === '/account-blocked' || location.pathname === '/conta-bloqueada';

  const blockedAccessAlert =
    '⚠️ ACESSO BLOQUEADO!\n\nSeu plano está vencido. Para acessar esta funcionalidade, por favor, regularize seu pagamento na página Financeiro.';

  /** Rotas permitidas com plano bloqueado: regularizar pagamento e perfil próprio. */
  const isSubscriptionPathAllowedWhenBlocked = (pathname) =>
    pathname === '/account-blocked' ||
    pathname === '/conta-bloqueada' ||
    pathname.startsWith('/profile') ||
    isSubscriptionAreaPath(pathname);
  // Avisos de trial/assinatura só para quem gerencia a assinatura (proprietário da conta)
  const canSeeSubscriptionWarnings = !isSuperAdminUser && canManageSubscription(user);
  const bannerVisible = canSeeSubscriptionWarnings && planActive && trialActive && !isBlockedRoute;

  const [anchorEl, setAnchorEl] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [trialEndingModalOpen, setTrialEndingModalOpen] = useState(false);
  const [profileUrl, setProfileUrl] = useState(null);
  const [volume, setVolume] = useState(() => {
    const raw = localStorage.getItem('volume');
    const num = raw !== null && raw !== '' ? Number(raw) : NaN;
    return !Number.isFinite(num) ? 1 : Math.min(1, Math.max(0, num));
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [submenuPopoverAnchor, setSubmenuPopoverAnchor] = useState(null);
  const [submenuPopoverMenuKey, setSubmenuPopoverMenuKey] = useState(null);
  const [helpAnchor, setHelpAnchor] = useState(null);
  const [whatsappSupportPhone, setWhatsappSupportPhone] = useState('');
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);

  const handleHelpOpen = (e) => setHelpAnchor(e.currentTarget);
  const handleHelpClose = () => setHelpAnchor(null);

  useEffect(() => {
    getSetting('whatsappSupportPhone')
      .then((data) => {
        if (data?.value) setWhatsappSupportPhone(data.value);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMobile) {
      setDrawerExpanded(false);
      return;
    }
    if (!drawerInitialized.current && user) {
      drawerInitialized.current = true;
      setDrawerExpanded(user.defaultMenu !== 'closed');
    }
  }, [user, isMobile]);

  useEffect(() => {
    setExpandedMenus((prev) => ({
      ...prev,
      sistema: location.pathname.startsWith('/system') || location.pathname.startsWith('/sistema'),
      superAdmin: location.pathname.startsWith('/super-admin'),
      ajuda: location.pathname === '/helps' || location.pathname === '/tutorial-videos',
      vendas: location.pathname.startsWith('/sales') || location.pathname.startsWith('/vendas'),
      marketing:
        location.pathname.startsWith('/marketing') || location.pathname.startsWith('/campanhas'),
      contatos:
        location.pathname.startsWith('/contacts') || location.pathname.startsWith('/contatos'),
      atendimentos:
        location.pathname.startsWith('/tickets') || location.pathname.startsWith('/chats'),
      automacoes: location.pathname.startsWith('/automations'),
      crm: location.pathname.startsWith('/crm'),
      tasks: location.pathname.startsWith('/tasks'),
    }));
  }, [location.pathname]);

  useEffect(() => {
    if (user?.profileImage) {
      const nextUrl = `${backendUrl}/public/company${user.companyId}/profile-images/${user.profileImage}`;
      setProfileUrl((current) => (current !== nextUrl ? nextUrl : current));
    } else {
      setProfileUrl((current) => (current !== null ? null : current));
    }
  }, [user?.profileImage, user?.companyId]);

  // BLOQUEIO PARA PLANOS VENCIDOS (ignorado para Super Admin)
  useEffect(() => {
    if (isSuperAdminUser) return;

    // Se ainda está carregando informações do plano, não faz nada
    if (planLoading || loading) return;

    if (!planActive) {
      if (
        location.pathname.startsWith('/subscription/creditos') ||
        location.pathname.startsWith('/subscription/ai-credits')
      ) {
        history.replace(SUBSCRIPTION_PATH_BY_SECTION[SUBSCRIPTION_SECTIONS.FATURA]);
        return;
      }

      if (isSubscriptionAreaPath(location.pathname) && !canManageSubscriptionAccess) {
        history.push('/account-blocked');
        setBlockModalOpen(true);
        setTrialEndingModalOpen(false);
        return;
      }

      // Conta bloqueada: só planos/faturas; demais rotas → página de bloqueio.
      if (!isSubscriptionPathAllowedWhenBlocked(location.pathname)) {
        history.push('/account-blocked');
        setBlockModalOpen(true);
        setTrialEndingModalOpen(false);
      }
    }
  }, [
    planActive,
    planLoading,
    loading,
    location.pathname,
    history,
    isSuperAdminUser,
    canManageSubscriptionAccess,
  ]);

  // Remove push subscription quando conta bloqueada (fatura vencida) — evita notificações indevidas
  useEffect(() => {
    if (!isSuperAdminUser && !planActive) {
      unsubscribeAndUnregister();
    }
  }, [planActive, isSuperAdminUser]);

  // Usuário sem nenhuma permissão configurada → redireciona para página de aviso
  useEffect(() => {
    if (!user || loading || planLoading) return;
    if (user.super || user.isOwner) return;
    if (location.pathname === '/no-permission') return;
    if (location.pathname === '/profile') return;
    if (userPermissions.size === 0) {
      history.replace('/no-permission');
    }
  }, [user, loading, planLoading, userPermissions, location.pathname, history]);

  // Modal "período de teste acabando" — usa user.trial (SerializeUser do backend) como fonte de verdade
  useEffect(() => {
    if (!canSeeSubscriptionWarnings || !planActive) return;
    if (!user?.companyId) return;
    if (planLoading || loading) return;

    // Usa user.trial quando disponível (novo backend); fallback para dueDate durante migração
    const trial = user?.trial;
    const shouldShow = trial
      ? shouldShowTrialEndingModal(trial)
      : Boolean(user?.company?.dueDate && trialActive && trialExpiringSoon);

    if (!shouldShow) return;
    if (wasTrialEndingModalShownToday(user.companyId)) return;

    setTrialEndingModalOpen(true);
  }, [
    user?.companyId,
    user?.trial,
    user?.company?.dueDate,
    planActive,
    planLoading,
    loading,
    canSeeSubscriptionWarnings,
    trialActive,
    trialExpiringSoon,
  ]);

  const handleTrialEndingModalClose = () => {
    markTrialEndingModalShownToday(user?.companyId);
    setTrialEndingModalOpen(false);
  };

  const handleUserMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
    setUserMenuOpen(true);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
    setUserMenuOpen(false);
  };

  const handleOpenUserProfile = () => {
    handleUserMenuClose();
    history.push('/profile');
  };

  const handleOpenSettings = () => {
    handleUserMenuClose();
    history.push(APP_PATHS.SETTINGS);
  };

  const handleOpenUsers = () => {
    handleUserMenuClose();
    history.push(APP_PATHS.USERS);
  };

  const handleOpenRoles = () => {
    handleUserMenuClose();
    history.push(APP_PATHS.ROLES);
  };

  const handleOpenSubscription = () => {
    handleUserMenuClose();
    history.push(APP_PATHS.SUBSCRIPTION);
  };

  const handleLogoutClick = () => {
    handleLogout();
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const saveDrawerPreference = (expanded) => {
    if (!user?.id || isMobile) return;
    updateMyProfile({ defaultMenu: expanded ? 'open' : 'closed' }).catch(() => {});
  };

  const isSubItemDisabled = (parentItem, sub) => Boolean(sub.disabled ?? parentItem.disabled);

  const handleSubmenuItemSelect = (parentItem, sub) => {
    if (isSubItemDisabled(parentItem, sub)) {
      alert(blockedAccessAlert);
      return;
    }
    history.push(sub.path);
    setSubmenuPopoverAnchor(null);
    setSubmenuPopoverMenuKey(null);
    if (isMobile) setMobileOpen(false);
  };

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Fechar o sheet "Mais" ao navegar
  useEffect(() => {
    setMoreSheetOpen(false);
  }, [location.pathname]);

  /**
   * Itens do sheet "Mais" — adicione novos objetos aqui para escalar o menu mobile.
   * Formato: { title, path, icon }
   */
  const mobileSheetItems = useMemo(
    () => [{ title: 'Apps', path: '/apps', icon: <AppsIcon /> }],
    []
  );

  /** Sheet "Mais": ativo se aberto OU se a rota atual pertence a um item do sheet */
  const isMoreActive = moreSheetOpen || mobileSheetItems.some((i) => isActivePath(i.path));

  // Detectar se está dentro de uma conversa de ticket (para ocultar menu mobile e header)
  const isInsideTicketConversation = /\/tickets\/conversation\/[^/]+$/i.test(location.pathname);

  // Detectar se está no Flow Builder (para ocultar menu lateral)
  const isFlowBuilderPage =
    location.pathname === '/automations/flowbuilder' ||
    location.pathname.startsWith('/automations/flowbuilder/');

  /** Página de conversas internas: sem menu mobile inferior (layout tipo messenger). */
  const isInternalChatsPage = location.pathname.startsWith('/chats');
  const hideMobileBottomNav =
    isInsideTicketConversation || isFlowBuilderPage || isInternalChatsPage;

  const staticMenuItems = useMemo(() => {
    // Página /chats: super sempre; owner só com chat interno no plano.
    const showInternalChatAdminPage =
      isSuperAdminUser || (isOwnerUser && !planLoading && planInternalChat);

    return [
      {
        title: 'Início',
        path: '/inicio',
        icon: <HomeIcon />,
        exact: true,
      },
      {
        title: 'Dashboard',
        path: '/dashboard',
        icon: <DashboardIcon />,
        exact: true,
        disabled: !planActive,
        permission: 'dashboard:read',
      },
      {
        title: 'CRM',
        path: '/crm',
        icon: <ViewKanbanIcon />,
        disabled: !planActive,
        permission: 'crm:read',
        ...(isGodMode ||
        userPermissions.has('crm:manage_pipelines') ||
        userPermissions.has('crm:manage_automations')
          ? {
              subItems: [
                {
                  title: 'Pipeline',
                  path: '/crm/pipeline',
                  icon: <ViewKanbanIcon />,
                  permission: 'crm:read',
                },
                {
                  title: 'Funis',
                  path: '/crm/funnels',
                  icon: <AccountTreeIcon />,
                  permission: 'crm:manage_pipelines',
                },
                {
                  title: 'Automações',
                  path: '/crm/automations',
                  icon: <FlashOnIcon />,
                  permission: 'crm:manage_automations',
                },
              ],
            }
          : {}),
      },
      ...(isSuperAdminUser || (!planLoading && userCanUseTasks)
        ? [
            {
              title: 'Tarefas',
              path: '/tasks',
              icon: <TaskAltIcon />,
              disabled: !planActive,
              permission: 'tasks:read',
            },
          ]
        : []),
      {
        title: 'Atendimento',
        path: '/tickets',
        icon: <SupportAgentIcon />,
        disabled: !planActive,
        subItems: [
          {
            title: 'Conversas',
            path: '/tickets/conversation',
            icon: <ChatIcon />,
            permission: 'tickets:read',
          },
          {
            title: 'Mensagens rápidas',
            path: '/tickets/quick-messages',
            icon: <FlashOnIcon />,
            permission: 'quick_messages:read',
          },
          {
            title: 'Departamentos (fila)',
            path: '/tickets/queues',
            icon: <QueueIcon />,
            permission: 'queues:read',
          },
          {
            title: 'Tags',
            path: '/tickets/tags',
            icon: <LocalOfferIcon />,
            permission: 'tags:read',
          },
          {
            title: 'Conexões',
            path: '/tickets/channels',
            icon: <DeviceHubIcon />,
            permission: 'connections:read',
          },
          ...(isSuperAdminUser || (!planLoading && planSchedules)
            ? [
                {
                  title: 'Mensagens agendadas',
                  path: '/tickets/schedules',
                  icon: <ScheduleIcon />,
                  permission: 'schedules:read',
                },
              ]
            : []),
          ...(showInternalChatAdminPage
            ? [
                {
                  title: 'Chats internos',
                  path: '/chats',
                  icon: <ForumIcon />,
                  // Sem permission key no catálogo RBAC: só owner (com plano) ou super.
                  alwaysVisible: true,
                },
              ]
            : []),
        ],
      },
      {
        title: 'Vendas',
        path: '/sales',
        icon: <BusinessCenterIcon />,
        disabled: !planActive,
        subItems: [
          {
            title: 'Clientes',
            path: '/sales/clients',
            icon: <PersonIcon />,
            permission: 'clients:read',
          },
          ...(isSuperAdminUser || (!planLoading && userCanUseProducts)
            ? [
                {
                  title: 'Produtos',
                  path: '/sales/products',
                  icon: <ShoppingCartIcon />,
                  permission: 'products:read',
                },
                {
                  title: 'Categorias',
                  path: '/sales/categories',
                  icon: <CategoryIcon />,
                  permission: 'products:manage_categories',
                },
                {
                  title: 'Atributos',
                  path: '/sales/attributes',
                  icon: <TuneIcon />,
                  permission: 'products:manage_attributes',
                },
              ]
            : []),
          {
            title: 'Serviços',
            path: '/sales/services',
            icon: <BuildIcon />,
            permission: 'services:read',
          },
        ],
      },
      {
        title: 'Marketing',
        path: '/marketing',
        icon: <CampaignIcon />,
        disabled: !planActive,
        subItems: [
          {
            title: 'FollowUp',
            path: '/marketing/flowup',
            icon: <TrendingUpIcon />,
            permission: 'followup:read',
          },
          {
            title: 'Tempo sem resposta',
            path: '/marketing/no-response',
            icon: <TimerIcon />,
            permission: 'no_response:read',
          },
          {
            title: 'Campanhas por frase',
            path: '/marketing/campaign-by-phrase',
            icon: <MessageIcon />,
            permission: 'campaigns:read',
          },
          ...(isSuperAdminUser || hasApp('mass_dispatch')
            ? [
                {
                  title: 'Disparo em massa',
                  path: '/marketing/mass-shooting',
                  icon: <SendIcon />,
                  permission: 'mass_dispatch:read',
                },
              ]
            : []),
        ],
      },
      {
        title: 'Contatos',
        path: '/contacts',
        icon: <ContactsIcon />,
        disabled: !planActive,
        subItems: [
          {
            title: 'Listas segmentadas (tags)',
            path: '/contacts/contact-lists',
            icon: <ListIcon />,
            permission: 'contacts:group',
          },
          {
            title: 'Lista de contatos',
            path: '/contacts/import-contacts',
            icon: <ContactsIcon />,
            permission: 'contacts:read',
          },
          // {
          //   title: 'Configurações de Contatos',
          //   path: '/contacts/settings',
          //   icon: <SettingsIcon />,
          // },
        ],
      },
      {
        title: 'Automações',
        path: '/automations',
        icon: <ExtensionIcon />,
        disabled: !planActive,
        subItems: [
          ...(isSuperAdminUser || (!planLoading && planOpenAi)
            ? [
                {
                  title: 'IA Workflows',
                  path: '/automations/ia-workflows',
                  icon: <AccountTreeIcon />,
                  permission: 'ia:read',
                },
                {
                  title: 'Agentes de IA',
                  path: '/automations/agents',
                  icon: <PersonIcon />,
                  permission: 'ia:read',
                },
              ]
            : []),
          {
            title: 'Construtor de fluxo',
            path: '/automations/flowbuilders',
            icon: <AccountTreeIcon />,
            permission: 'flowbuilder:read',
          },
          ...(isSuperAdminUser || (!planLoading && planIntegrations)
            ? [
                {
                  title: 'Integrações',
                  path: '/automations/integration',
                  icon: <DeviceHubIcon />,
                  permission: 'integrations:read',
                },
              ]
            : []),
          ...(isSuperAdminUser
            ? [
                {
                  title: 'Mensagens automáticas',
                  path: '/automations/message',
                  icon: <MessageIcon />,
                },
              ]
            : []),
        ],
      },
      {
        title: 'Apps',
        path: '/apps',
        icon: <AppsIcon />,
        disabled: !planActive,
      },
      ...(isSuperAdminUser
        ? [
            {
              title: 'White Label',
              path: '/super-admin',
              icon: <AdminPanelSettingsIcon />,
              disabled: !planActive,
              subItems: [
                { title: 'Empresas', path: '/super-admin/companies', icon: <BusinessIcon /> },
                { title: 'Faturas', path: '/super-admin/invoices', icon: <ReceiptIcon /> },
                {
                  title: 'Financeiro (Relatórios)',
                  path: '/super-admin/financial',
                  icon: <ReceiptIcon />,
                },
                { title: 'Planos', path: '/super-admin/plans', icon: <LoyaltyIcon /> },
                { title: 'Apps', path: '/super-admin/apps', icon: <AppsIcon /> },
                { title: 'Personalização', path: '/super-admin/whitelabel', icon: <PaletteIcon /> },
                {
                  title: 'Credenciais',
                  path: '/super-admin/payment-credentials',
                  icon: <VpnKeyIcon />,
                },
                { title: 'Configurações', path: '/super-admin/settings', icon: <SettingsIcon /> },
                {
                  title: 'Créditos de IA',
                  path: '/super-admin/ai-credits',
                  icon: <SmartToyIcon />,
                },
                {
                  title: 'Notificações',
                  path: '/super-admin/notifications',
                  icon: <NotificationsActiveIcon />,
                },
                {
                  title: 'Monitoramento',
                  path: '/super-admin/monitoring',
                  icon: <MonitorHeartIcon />,
                },
              ],
            },
          ]
        : []),
      {
        title: 'Central de Ajuda',
        path: '/helps',
        icon: <HelpOutlineIcon />,
        disabled: !planActive,
        subItems: [
          { title: 'Ajuda', path: '/helps', icon: <HelpOutlineIcon />, alwaysVisible: true },
          ...(isSuperAdminUser
            ? [{ title: 'Vídeos Tutoriais', path: '/tutorial-videos', icon: <VideoLibraryIcon /> }]
            : []),
        ],
      },
    ];
  }, [
    planActive,
    hasApp,
    location.pathname,
    isSuperAdminUser,
    isOwnerUser,
    isGodMode,
    user?.profile,
    planLoading,
    planOpenAi,
    planIntegrations,
    planSchedules,
    planInternalChat,
    allowAiPlatformCredits,
    userPermissions,
  ]);

  // Exibe apenas itens cujas permissions o usuário possui.
  // Só owner/super (god mode) veem staticMenuItems direto; admin comum respeita RBAC.
  const menuItems = useMemo(() => {
    if (isGodMode) return staticMenuItems;
    return staticMenuItems
      .map((item) => {
        if (item.alwaysVisible) return item;
        if (item.subItems) {
          const allowedSubs = item.subItems.filter(
            (sub) => sub.alwaysVisible || (sub.permission && userPermissions.has(sub.permission))
          );
          if (allowedSubs.length === 0) return null;
          return { ...item, subItems: allowedSubs };
        }
        if (!item.permission) return null;
        return userPermissions.has(item.permission) ? item : null;
      })
      .filter(Boolean);
  }, [staticMenuItems, isGodMode, userPermissions]);

  const MenuItemWithTooltip = ({ path, icon, title, exact = false, disabled = false }) => {
    const isActive = exact
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(path + '/');

    const handleClick = (event) => {
      if (disabled) {
        event.preventDefault();
        // Mostra alerta ao tentar acessar com plano vencido
        alert(blockedAccessAlert);
        return;
      }
      if (!isActive) {
        history.push(path);
      }
    };

    const item = (
      <ListItem
        button
        disabled={disabled}
        onClick={handleClick}
        className={`${classes.menuItem} ${isActive ? 'active' : ''}`}
      >
        <ListItemIcon className={classes.menuIcon}>{icon}</ListItemIcon>
        {showMenuLabels && <ListItemText primary={title} classes={{ primary: classes.menuText }} />}
      </ListItem>
    );

    if (showMenuLabels) {
      return item;
    }

    return (
      <Tooltip title={title} placement="right" arrow>
        {item}
      </Tooltip>
    );
  };

  const DrawerContent = () => (
    <div>
      <div className={classes.drawerHeader}>
        {drawerExpanded ? (
          <>
            <div className={classes.drawerHeaderLogo}>
              <div className={classes.logo} onClick={() => history.push('/dashboard')}>
                {theme.appLogoLight || theme.calculatedLogoLight?.() ? (
                  <img
                    src={
                      theme.calculatedLogoLight ? theme.calculatedLogoLight() : theme.appLogoLight
                    }
                    alt="Logo"
                    className={classes.companyLogo}
                  />
                ) : (
                  <DashboardIcon className={classes.logoIcon} />
                )}
              </div>
            </div>
            <Tooltip title="Recolher menu" placement="right">
              <IconButton
                onClick={() => {
                  if (!isMobile) {
                    setDrawerExpanded(false);
                    saveDrawerPreference(false);
                  }
                }}
                className={classes.menuToggleButton}
                size="small"
                disableRipple
              >
                <FormatIndentDecreaseIcon className={classes.menuToggleIconCombo} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip title="Abrir menu" placement="right">
            <IconButton
              onClick={() => {
                if (!isMobile) {
                  setDrawerExpanded(true);
                  saveDrawerPreference(true);
                }
              }}
              className={classes.menuToggleButton}
              size="small"
              disableRipple
            >
              <FormatIndentIncreaseIcon className={classes.menuToggleIconCombo} />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <div className={classes.sidebarContent}>
        <List className={classes.menuList}>
          <div className={classes.menuSectionLabel}>Principais</div>
          {menuItems.map((item) => {
            if (item.subItems) {
              const menuKey =
                item.path === '/system'
                  ? 'sistema'
                  : item.path === '/super-admin'
                    ? 'superAdmin'
                    : item.path === '/helps'
                      ? 'ajuda'
                      : item.path === '/sales'
                        ? 'vendas'
                        : item.path === '/marketing'
                          ? 'marketing'
                          : item.path === '/contacts'
                            ? 'contatos'
                            : item.path === '/tickets'
                              ? 'atendimentos'
                              : item.path === '/automations'
                                ? 'automacoes'
                                : item.path === '/crm'
                                  ? 'crm'
                                  : item.path === '/tasks'
                                    ? 'tasks'
                                    : null;
              const isMenuActive =
                item.path === '/helps'
                  ? location.pathname === '/helps' || location.pathname === '/tutorial-videos'
                  : item.path === '/marketing'
                    ? location.pathname.startsWith('/marketing') ||
                      location.pathname.startsWith('/campanhas')
                    : item.path === '/tickets'
                      ? location.pathname.startsWith('/tickets') ||
                        location.pathname.startsWith('/chats')
                      : location.pathname === item.path ||
                        location.pathname.startsWith(item.path + '/');
              const isExpanded = menuKey ? expandedMenus[menuKey] : false;
              const handleParentClick = (event) => {
                if (item.disabled) {
                  alert(blockedAccessAlert);
                  return;
                }
                if (showMenuLabels) {
                  if (menuKey) {
                    setExpandedMenus((prev) => ({
                      ...prev,
                      [menuKey]: !prev[menuKey],
                    }));
                  }
                } else {
                  setSubmenuPopoverAnchor(event.currentTarget);
                  setSubmenuPopoverMenuKey(menuKey);
                }
              };
              const parentItem = (
                <ListItem
                  button
                  disabled={item.disabled}
                  onClick={handleParentClick}
                  className={`${classes.menuItem} ${isMenuActive ? 'active' : ''}`}
                >
                  <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
                  {showMenuLabels && (
                    <>
                      <ListItemText primary={item.title} classes={{ primary: classes.menuText }} />
                      {isExpanded ? (
                        <ExpandLessIcon className={classes.menuItemExpandIcon} />
                      ) : (
                        <ExpandMoreIcon className={classes.menuItemExpandIcon} />
                      )}
                    </>
                  )}
                </ListItem>
              );
              return (
                <React.Fragment key={item.path}>
                  {showMenuLabels ? (
                    parentItem
                  ) : (
                    <Tooltip title={item.title} placement="right" arrow>
                      {parentItem}
                    </Tooltip>
                  )}
                  {showMenuLabels && isExpanded && (
                    <div className={classes.subMenuWrapper}>
                      {item.subItems.map((sub) => {
                        const isSubActive =
                          location.pathname === sub.path ||
                          location.pathname.startsWith(sub.path + '/');
                        const subDisabled = isSubItemDisabled(item, sub);
                        const handleSubClick = () => {
                          if (subDisabled) {
                            alert(blockedAccessAlert);
                            return;
                          }
                          history.push(sub.path);
                        };
                        return (
                          <ListItem
                            key={sub.path}
                            button
                            disabled={subDisabled}
                            onClick={handleSubClick}
                            className={`${classes.subMenuItem} ${isSubActive ? 'active' : ''}`}
                          >
                            {sub.icon && (
                              <ListItemIcon className={classes.subMenuItemIcon}>
                                {sub.icon}
                              </ListItemIcon>
                            )}
                            <ListItemText
                              primary={sub.title}
                              classes={{ primary: classes.subMenuText }}
                            />
                          </ListItem>
                        );
                      })}
                    </div>
                  )}
                </React.Fragment>
              );
            }
            return <MenuItemWithTooltip key={item.path} {...item} />;
          })}
        </List>

        <Menu
          anchorEl={submenuPopoverAnchor}
          open={Boolean(submenuPopoverAnchor)}
          onClose={() => {
            setSubmenuPopoverAnchor(null);
            setSubmenuPopoverMenuKey(null);
          }}
          anchorReference="anchorPosition"
          anchorPosition={
            submenuPopoverAnchor
              ? {
                  left: collapsedDrawerWidth,
                  top: submenuPopoverAnchor.getBoundingClientRect().top,
                }
              : undefined
          }
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            className: classes.submenuPopoverPaper,
            elevation: 8,
          }}
          transitionDuration={200}
        >
          {(() => {
            const popoverParent = menuItems.find(
              (i) =>
                i.subItems &&
                ((i.path === '/system' && submenuPopoverMenuKey === 'sistema') ||
                  (i.path === '/super-admin' && submenuPopoverMenuKey === 'superAdmin') ||
                  (i.path === '/helps' && submenuPopoverMenuKey === 'ajuda') ||
                  (i.path === '/sales' && submenuPopoverMenuKey === 'vendas') ||
                  (i.path === '/marketing' && submenuPopoverMenuKey === 'marketing') ||
                  (i.path === '/contacts' && submenuPopoverMenuKey === 'contatos') ||
                  (i.path === '/tickets' && submenuPopoverMenuKey === 'atendimentos') ||
                  (i.path === '/automations' && submenuPopoverMenuKey === 'automacoes'))
            );
            if (!popoverParent?.subItems) return null;
            return popoverParent.subItems.map((sub) => {
              const subDisabled = isSubItemDisabled(popoverParent, sub);
              const isSubActive =
                location.pathname === sub.path || location.pathname.startsWith(sub.path + '/');
              return (
                <MenuItem
                  key={sub.path}
                  disabled={subDisabled}
                  onClick={() => handleSubmenuItemSelect(popoverParent, sub)}
                  className={`${classes.submenuPopoverItem} ${isSubActive ? 'active' : ''}`}
                >
                  {sub.icon && (
                    <ListItemIcon className={classes.subMenuItemIcon}>{sub.icon}</ListItemIcon>
                  )}
                  {sub.title}
                </MenuItem>
              );
            });
          })()}
        </Menu>
      </div>
    </div>
  );

  return (
    <div className={classes.root}>
      {/* Sidebar Desktop */}
      <Drawer
        className={classes.drawer}
        variant="permanent"
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <DrawerContent />
      </Drawer>

      {/* Sidebar Mobile */}
      <Drawer
        className={classes.mobileDrawer}
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <DrawerContent />
      </Drawer>

      {/* Banner de trial — fixo no topo do viewport, acima do header */}
      {bannerVisible && (
        <div className={classes.trialBannerWrapper}>
          <TrialBanner />
        </div>
      )}

      {/* Header - Sempre visível */}
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        className={classes.appBar}
        style={{ top: bannerVisible ? TRIAL_BANNER_HEIGHT : 0 }}
      >
        <Toolbar className={classes.toolbar}>
          {/* Seção Esquerda */}
          <div className={classes.headerLeft}>
            {/* Back Button - Visível apenas no Flow Builder */}
            {isFlowBuilderPage && (
              <IconButton
                className={classes.menuButton}
                onClick={() => history.push('/automations/flowbuilders')}
                edge="start"
                title="Voltar para lista de fluxos"
              >
                <ArrowBackIcon />
              </IconButton>
            )}

            {/* Menu Button Mobile - Oculto só no Flow Builder; na conversa o voltar fica no ticker */}
            {!isFlowBuilderPage && (
              <IconButton className={classes.menuButton} onClick={handleDrawerToggle} edge="start">
                <MenuIcon />
              </IconButton>
            )}

            {/* Busca - Oculto no mobile */}
            <div
              className={classes.searchContainer}
              onClick={() => setSearchModalOpen(true)}
              style={{ cursor: 'pointer' }}
            >
              <div className={classes.searchIcon}>
                <SearchIcon />
              </div>
              <InputBase
                placeholder="Buscar conversas..."
                classes={{
                  root: classes.inputRoot,
                  input: classes.inputInput,
                }}
                readOnly
                style={{ cursor: 'pointer' }}
              />
              <Button className={classes.searchButton}>
                <SearchIcon style={{ fontSize: 18 }} />
              </Button>
            </div>

            {/* Logo - Visível apenas no mobile. Usa o ícone do PWA (quadrado),
                evitando que o logo largo esprema/esconda os botões do header. */}
            <div className={classes.mobileLogo}>
              <img src={theme.appLogoFavicon || logo} alt="Logo" />
            </div>
          </div>

          {/* Seção Direita */}
          <div className={classes.headerRight}>
            {/* Chip de progresso da meta da equipe (CRM) */}
            <TeamGoalChip />

            {/* Botão Nova Conversa */}
            {canCreateTicket && (
              <Button
                variant="contained"
                onClick={() => setNewTicketModalOpen(true)}
                startIcon={<AddIcon style={{ fontSize: 18 }} />}
                style={{
                  backgroundColor: primaryColor,
                  color: '#fff',
                  borderRadius: 20,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 13,
                  padding: '6px 16px',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Nova conversa
              </Button>
            )}

            {/* Botão Atendimentos */}
            <IconButton
              className={classes.iconButton}
              onClick={() => history.push('/tickets/conversation')}
              title="Atendimentos"
            >
              <QuestionAnswerIcon style={{ fontSize: 24 }} />
            </IconButton>

            {/* Botão Ajuda */}
            <IconButton className={classes.iconButton} onClick={handleHelpOpen} title="Ajuda">
              <HelpOutlineIcon style={{ fontSize: 28 }} />
            </IconButton>

            {/* Menu Central de Suporte */}
            <Menu
              anchorEl={helpAnchor}
              open={Boolean(helpAnchor)}
              onClose={handleHelpClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              getContentAnchorEl={null}
              PaperProps={{
                style: {
                  borderRadius: 16,
                  width: 268,
                  padding: '14px 12px 12px',
                  boxShadow: '0 8px 28px rgba(0,0,0,0.13)',
                },
              }}
            >
              {/* Cabeçalho */}
              <Box style={{ paddingLeft: 2, marginBottom: 12 }}>
                <Typography
                  style={{ fontWeight: 700, fontSize: 14.5, color: '#111827', lineHeight: 1.3 }}
                >
                  Central de Suporte
                </Typography>
                <Typography
                  style={{ fontSize: 12, color: '#9ca3af', marginTop: 2, lineHeight: 1.4 }}
                >
                  Como podemos ajudar hoje?
                </Typography>
              </Box>

              {/* Item WhatsApp */}
              <Box
                onClick={() => {
                  if (whatsappSupportPhone)
                    window.open(`https://wa.me/${whatsappSupportPhone}`, '_blank');
                  handleHelpClose();
                }}
                style={{
                  backgroundColor: '#f0fdf4',
                  borderRadius: 10,
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                  marginBottom: 8,
                }}
              >
                <Box
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    backgroundColor: '#22c55e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <FaWhatsapp size={17} color="#fff" />
                </Box>
                <Box>
                  <Typography
                    style={{ fontWeight: 600, fontSize: 13, color: '#15803d', lineHeight: 1.3 }}
                  >
                    WhatsApp
                  </Typography>
                  <Typography style={{ fontSize: 11.5, color: '#86efac', lineHeight: 1.4 }}>
                    Atendimento em tempo real
                  </Typography>
                </Box>
              </Box>

              {/* Item Treinamento */}
              <Box
                onClick={() => {
                  history.push('/helps');
                  handleHelpClose();
                }}
                style={{
                  backgroundColor: '#eff6ff',
                  borderRadius: 10,
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                }}
              >
                <Box
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    backgroundColor: '#3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <OndemandVideoIcon style={{ fontSize: 18, color: '#fff' }} />
                </Box>
                <Box>
                  <Typography
                    style={{ fontWeight: 600, fontSize: 13, color: '#1d4ed8', lineHeight: 1.3 }}
                  >
                    Treinamento
                  </Typography>
                  <Typography style={{ fontSize: 11.5, color: '#93c5fd', lineHeight: 1.4 }}>
                    Vídeo aulas da ferramenta
                  </Typography>
                </Box>
              </Box>
            </Menu>

            {/* Botão Refresh */}
            <IconButton
              className={classes.iconButton}
              onClick={handleRefreshPage}
              title="Atualizar"
            >
              <CachedIcon style={{ fontSize: 24 }} />
            </IconButton>

            {/* Sino / Notificações */}
            <NotificationsDrawer
              volume={volume}
              setVolume={setVolume}
              primaryColor={primaryColor}
            />

            {/* Avatar do Usuário */}
            <Avatar className={classes.iconButton} src={profileUrl} onClick={handleUserMenuClick}>
              {!profileUrl && <PersonIcon style={{ fontSize: 30 }} />}
            </Avatar>
          </div>
        </Toolbar>
      </AppBar>

      {/* Menu do Usuário */}
      <Menu
        anchorEl={anchorEl}
        open={userMenuOpen}
        onClose={handleUserMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        getContentAnchorEl={null}
        PaperProps={{ className: classes.userMenuPaper }}
      >
        {/* Card de perfil */}
        <Box className={classes.userMenuProfile} onClick={handleOpenUserProfile}>
          <Avatar className={classes.userMenuAvatar}>
            {user?.name ? user.name.charAt(0).toUpperCase() : <PersonIcon />}
          </Avatar>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Typography className={classes.userMenuName}>{user?.name || 'Meu Perfil'}</Typography>
            <Typography className={classes.userMenuRole}>
              {user?.super
                ? 'Super Admin'
                : user?.profile === 'admin'
                  ? 'Administrador'
                  : 'Usuário'}
            </Typography>
          </Box>
          <Box className={classes.userMenuEditChip}>
            <Typography className={classes.userMenuEditChipText}>Editar</Typography>
          </Box>
        </Box>

        <Divider className={classes.userMenuDivider} />

        {/* Assinatura - só proprietário da conta (e super admin como vitrine) */}
        {canManageSubscription(user) && (
          <Box className={classes.userMenuAction} onClick={handleOpenSubscription}>
            <Box className={classes.userMenuActionIcon} style={{ backgroundColor: '#eff6ff' }}>
              <CreditCardIcon style={{ fontSize: 18, color: '#3b82f6' }} />
            </Box>
            <Box>
              <Typography className={classes.userMenuActionTitle}>Assinatura</Typography>
              <Typography className={classes.userMenuActionSubtitle}>Gerencie seu plano</Typography>
            </Box>
          </Box>
        )}

        {/* Configurações - god mode (owner/super) ou quem tem settings:read */}
        {(isGodMode || userPermissions.has('settings:read')) && (
          <Box className={classes.userMenuAction} onClick={handleOpenSettings}>
            <Box className={classes.userMenuActionIcon} style={{ backgroundColor: '#f5f3ff' }}>
              <SettingsIcon style={{ fontSize: 18, color: '#7c3aed' }} />
            </Box>
            <Box>
              <Typography className={classes.userMenuActionTitle}>Configurações</Typography>
              <Typography className={classes.userMenuActionSubtitle}>
                Preferências do sistema
              </Typography>
            </Box>
          </Box>
        )}

        {/* Usuários - visível se tiver users:read */}
        {(isGodMode || canReadUsers) && (
          <Box className={classes.userMenuAction} onClick={handleOpenUsers}>
            <Box className={classes.userMenuActionIcon} style={{ backgroundColor: '#ecfdf5' }}>
              <PeopleAltIcon style={{ fontSize: 18, color: '#059669' }} />
            </Box>
            <Box>
              <Typography className={classes.userMenuActionTitle}>Usuários</Typography>
              <Typography className={classes.userMenuActionSubtitle}>
                Gerencie sua equipe
              </Typography>
            </Box>
          </Box>
        )}

        {/* Permissões - owner e super (alinhado ao guard ownerOnly da rota) */}
        {Boolean(user?.isOwner || user?.super) && (
          <Box className={classes.userMenuAction} onClick={handleOpenRoles}>
            <Box className={classes.userMenuActionIcon} style={{ backgroundColor: '#e3f2fd' }}>
              <SecurityIcon style={{ fontSize: 18, color: '#1976d2' }} />
            </Box>
            <Box>
              <Typography className={classes.userMenuActionTitle}>Permissões</Typography>
              <Typography className={classes.userMenuActionSubtitle}>Perfis de acesso</Typography>
            </Box>
          </Box>
        )}

        {/* Sair */}
        <Box
          className={`${classes.userMenuAction} ${classes.userMenuActionLogout}`}
          onClick={handleLogoutClick}
        >
          <Box className={classes.userMenuActionIcon} style={{ backgroundColor: '#fef2f2' }}>
            <ExitToAppIcon style={{ fontSize: 18, color: '#ef4444' }} />
          </Box>
          <Box>
            <Typography
              className={`${classes.userMenuActionTitle} ${classes.userMenuActionLogoutTitle}`}
            >
              {isMobileSession ? 'Sair do app' : 'Sair'}
            </Typography>
            <Typography className={classes.userMenuActionSubtitle}>Encerrar sessão</Typography>
          </Box>
        </Box>
      </Menu>

      {/* Modal Nova Conversa */}
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
          setNewTicketModalOpen(false);
          if (ticket?.uuid) {
            setTabOpen('open');
            setCurrentTicket(ticket);
          }
        }}
      />

      {/* Modal de Notificações */}
      <NotificationModal
        open={notificationModalOpen}
        onClose={() => setNotificationModalOpen(false)}
      />

      {/* Modal de bloqueio (fatura vencida) */}
      <BlockedModal open={blockModalOpen} onClose={() => setBlockModalOpen(false)} />

      {/* Modal período de teste acabando — plano ativo; nunca quando bloqueado */}
      <TrialEndingModal
        open={trialEndingModalOpen && planActive && !blockModalOpen}
        onClose={handleTrialEndingModalClose}
        trial={user?.trial}
        dueDate={user?.company?.dueDate}
      />

      {/* PWA: dynamic head, install prompt, e aviso para desinstalar/reinstalar quando a versão do ícone sobe */}
      <PwaHeadUpdater />
      <PWAInstallPrompt />
      <PwaReinstallModal />

      {/* Modal de Busca de Conversas */}
      <SearchTicketModal open={searchModalOpen} onClose={() => setSearchModalOpen(false)} />

      {/* Widget global: todos os usuários com chat no plano (super ignora plano). Oculto em /chats (página full). */}
      <InternalChatWidget
        disabled={
          (!isSuperAdminUser && !planInternalChat) || location.pathname.startsWith('/chats')
        }
      />

      {/* Painel lateral de presença de usuários — god mode (owner/super) ou quem tem users:read */}
      {(isGodMode || userPermissions.has('users:read')) && <UsersPresencePanel />}

      {/* Conteúdo Principal: scroll só dentro do wrapper; global header e (em páginas) page header ficam fixos */}
      <main
        id="app-main-content"
        className={`${classes.content} ${bannerVisible ? classes.contentWithBanner : ''} ${!hideMobileBottomNav ? classes.contentWithMobileNav : ''}`}
      >
        <div
          className={`${classes.contentScrollWrapper} ${hideMobileBottomNav ? classes.contentScrollWrapperNoNavPadding : ''}`}
        >
          <div className={classes.contentScrollInner}>
            <Suspense
              fallback={
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="280px"
                  width="100%"
                >
                  <CircularProgress size={36} />
                </Box>
              }
            >
              {children}
            </Suspense>
          </div>
        </div>
      </main>

      {/* Menu Mobile Fixo - Oculto no desktop, em conversa de ticket, Flow Builder e /chats */}
      {!hideMobileBottomNav && (
        <div className={classes.mobileBottomNav}>
          {/* Botão 1 - Dashboard */}
          <div
            className={`${classes.mobileNavItem} ${isActivePath('/dashboard') ? 'active' : ''}`}
            onClick={() => history.push('/dashboard')}
          >
            <DashboardIcon className={classes.mobileNavIcon} />
            <span className={classes.mobileNavLabel}>Dashboard</span>
          </div>

          {/* Botão 2 - Contatos (página Lista de contatos) */}
          <div
            className={`${classes.mobileNavItem} ${isActivePath('/contacts/import-contacts') ? 'active' : ''}`}
            onClick={() => history.push('/contacts/import-contacts')}
          >
            <ContactsIcon className={classes.mobileNavIcon} />
            <span className={classes.mobileNavLabel}>Contatos</span>
          </div>

          {/* Botão Home (Centro) - Tickets */}
          <div
            className={classes.mobileNavHomeBtn}
            onClick={() => history.push('/tickets/conversation')}
          >
            <ChatIcon className={classes.mobileNavHomeIcon} />
          </div>

          {/* Botão 4 - Conexões (somente com connections:read) */}
          {(isGodMode || userPermissions.has('connections:read')) && (
            <div
              className={`${classes.mobileNavItem} ${isActivePath('/tickets/channels') ? 'active' : ''}`}
              onClick={() => history.push('/tickets/channels')}
            >
              <DeviceHubIcon className={classes.mobileNavIcon} />
              <span className={classes.mobileNavLabel}>Conexões</span>
            </div>
          )}

          {/* Botão 5 - Mais (abre o sheet com itens extras) */}
          <div
            className={`${classes.mobileNavItem} ${isMoreActive ? 'active' : ''}`}
            onClick={() => setMoreSheetOpen((prev) => !prev)}
          >
            <MenuIcon className={classes.mobileNavIcon} />
            <span className={classes.mobileNavLabel}>Mais</span>
          </div>
        </div>
      )}

      {/* Backdrop do sheet "Mais" */}
      {!hideMobileBottomNav && moreSheetOpen && (
        <div className={classes.mobileNavSheetBackdrop} onClick={() => setMoreSheetOpen(false)} />
      )}

      {/* Sheet "Mais" — grid de atalhos escalável */}
      {!hideMobileBottomNav && moreSheetOpen && (
        <div className={classes.mobileNavSheet}>
          <div className={classes.mobileNavSheetHandle} />
          <div className={classes.mobileNavSheetTitle}>Atalhos</div>
          <div className={classes.mobileNavSheetGrid}>
            {mobileSheetItems.map((item) => (
              <div
                key={item.path}
                className={`${classes.mobileNavSheetItem} ${isActivePath(item.path) ? 'active' : ''}`}
                onClick={() => {
                  history.push(item.path);
                  setMoreSheetOpen(false);
                }}
              >
                {item.icon}
                <span className={classes.mobileNavSheetItemLabel}>{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoggedInLayout;
